import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getOrganizations } from '../../redux/lutien';
import {
  fetchTriggersByOrganization,
  deleteTrigger,
  updateTrigger,
  fetchSetupTemplates,
  fetchTriggerDetails
} from '../../redux/notificationCenter';
import { RootState } from '../../redux/store';
import { Notifications } from '../../services/Hub/notificationCenter/notificationCenterApi';
import {
  OrganizationNotificationTrigger,
  NotificationSetupTemplate,
  PaginatedNotificationSetupTemplateDTO
} from '../../services/Hub/notificationCenter/types';
import { default as OrganizationNotificationsView } from './OrganizationNotificationsView';

export default function OrganizationNotificationsContainer() {
  const dispatch = useDispatch();
  const { triggers, triggersPagination, isLoading, error } = useSelector(
    (state: RootState) => state.notificationCenter
  );
  const { organizations, isLoading: isLoadingOrganizations } = useSelector(
    (state: RootState) => state.lutien
  );
  const { selectedTriggerDetails, isLoadingTriggerDetails } = useSelector(
    (state: RootState) => state.notificationCenter
  );

  const [selectedOrganization, setSelectedOrganization] = useState<string | null>(null);
  const [showSetupTemplatesModal, setShowSetupTemplatesModal] = useState(false);
  const [selectedTrigger, setSelectedTrigger] = useState<OrganizationNotificationTrigger | null>(
    null
  );
  const [showEditTriggerModal, setShowEditTriggerModal] = useState(false);
  const [setupTemplatesData, setSetupTemplatesData] =
    useState<PaginatedNotificationSetupTemplateDTO>({
      items: [],
      total: 0,
      currentPage: 1,
      itemsPerPage: 10
    });
  const [isCreatingTrigger, setIsCreatingTrigger] = useState(false);
  const [isLoadingSetupTemplates, setIsLoadingSetupTemplates] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    (dispatch as any)(getOrganizations());
  }, [dispatch]);

  useEffect(() => {
    if (selectedOrganization) {
      (dispatch as any)(
        fetchTriggersByOrganization({
          organizationId: selectedOrganization,
          page: 1,
          itemsPerPage: 10
        })
      );
    }
  }, [dispatch, selectedOrganization]);

  useEffect(() => {
    if (showSetupTemplatesModal) {
      loadSetupTemplates(1, 8);
    }
  }, [showSetupTemplatesModal]);

  const loadSetupTemplates = async (page: number, itemsPerPage: number) => {
    setIsLoadingSetupTemplates(true);
    try {
      const result = await (dispatch as any)(
        fetchSetupTemplates({
          includeInactive: false,
          page,
          itemsPerPage
        })
      );
      setSetupTemplatesData(
        result.payload || {
          items: [],
          total: 0,
          currentPage: page,
          itemsPerPage
        }
      );
    } catch (error) {
      console.error('Error loading setup templates:', error);
    } finally {
      setIsLoadingSetupTemplates(false);
    }
  };

  const handleOrganizationChange = (orgId: string) => {
    setSelectedOrganization(orgId);
  };

  const handleEditTrigger = async (trigger: OrganizationNotificationTrigger) => {
    try {
      await (dispatch as any)(fetchTriggerDetails(trigger.id));
      setSelectedTrigger(trigger);
      setShowEditTriggerModal(true);
    } catch (error) {
      console.error('Error fetching trigger details:', error);
      setSelectedTrigger(trigger);
      setShowEditTriggerModal(true);
    }
  };

  const handleDeleteTrigger = async (triggerId: string) => {
    try {
      await (dispatch as any)(deleteTrigger(triggerId));
      if (selectedOrganization) {
        (dispatch as any)(
          fetchTriggersByOrganization({
            organizationId: selectedOrganization,
            page: triggersPagination.currentPage,
            itemsPerPage: triggersPagination.itemsPerPage
          })
        );
      }
    } catch (error) {
      console.error('Error deleting trigger:', error);
    }
  };

  const handleUpdateTrigger = async (triggerData: Partial<OrganizationNotificationTrigger>) => {
    try {
      await (dispatch as any)(
        updateTrigger({
          id: triggerData.id!,
          triggerData
        })
      );
      setShowEditTriggerModal(false);
      setSelectedTrigger(null);
      if (selectedOrganization) {
        (dispatch as any)(
          fetchTriggersByOrganization({
            organizationId: selectedOrganization,
            page: triggersPagination.currentPage,
            itemsPerPage: triggersPagination.itemsPerPage
          })
        );
      }
    } catch (error) {
      console.error('Error updating trigger:', error);
    }
  };

  const handleOpenSetupTemplatesModal = () => {
    setShowSetupTemplatesModal(true);
  };

  const handleCloseSetupTemplatesModal = () => {
    setShowSetupTemplatesModal(false);
  };

  const handleCloseEditTriggerModal = () => {
    setShowEditTriggerModal(false);
    setSelectedTrigger(null);
  };

  const handlePageChange = useCallback(
    (page: number, itemsPerPage: number) => {
      if (selectedOrganization) {
        (dispatch as any)(
          fetchTriggersByOrganization({
            organizationId: selectedOrganization,
            page,
            itemsPerPage,
            name: searchTerm
          })
        );
      }
    },
    [selectedOrganization, searchTerm, dispatch]
  );

  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);
      if (selectedOrganization) {
        (dispatch as any)(
          fetchTriggersByOrganization({
            organizationId: selectedOrganization,
            page: 1,
            itemsPerPage: triggersPagination.itemsPerPage,
            name: term
          })
        );
      }
    },
    [selectedOrganization, triggersPagination.itemsPerPage, dispatch]
  );

  const handleCreateTriggerFromTemplate = async (template: NotificationSetupTemplate) => {
    if (!selectedOrganization) {
      console.error('No organization selected');
      return;
    }

    setIsCreatingTrigger(true);
    try {
      const triggerData = {
        organizationId: selectedOrganization,
        notificationSetupTemplateId: template.id,
        recipientUserIds: template.recipientUserIds?.static || [],
        name: `${template.name} - Trigger`,
        description: template.description,
        source: template.source,
        conditions: template.conditions,
        mongoQuery: template.mongoQuery,
        isActive: true
      };

      await Notifications.triggers.create(triggerData);

      setShowSetupTemplatesModal(false);
      if (selectedOrganization) {
        (dispatch as any)(
          fetchTriggersByOrganization({
            organizationId: selectedOrganization,
            page: triggersPagination.currentPage,
            itemsPerPage: triggersPagination.itemsPerPage
          })
        );
      }
    } catch (error) {
      console.error('Error creating trigger from template:', error);
    } finally {
      setIsCreatingTrigger(false);
    }
  };

  return (
    <OrganizationNotificationsView
      selectedOrganization={selectedOrganization}
      onOrganizationChange={handleOrganizationChange}
      triggers={triggers}
      triggersPagination={triggersPagination}
      isLoading={isLoading}
      error={error}
      organizations={organizations}
      isLoadingOrganizations={isLoadingOrganizations}
      setupTemplatesData={setupTemplatesData}
      showSetupTemplatesModal={showSetupTemplatesModal}
      showEditTriggerModal={showEditTriggerModal}
      selectedTrigger={selectedTriggerDetails || selectedTrigger}
      isLoadingTriggerDetails={isLoadingTriggerDetails}
      isCreatingTrigger={isCreatingTrigger}
      onEditTrigger={handleEditTrigger}
      onDeleteTrigger={handleDeleteTrigger}
      onUpdateTrigger={handleUpdateTrigger}
      onPageChange={handlePageChange}
      onSearch={handleSearch}
      onOpenSetupTemplatesModal={handleOpenSetupTemplatesModal}
      onCloseSetupTemplatesModal={handleCloseSetupTemplatesModal}
      onCloseEditTriggerModal={handleCloseEditTriggerModal}
      onCreateTriggerFromTemplate={handleCreateTriggerFromTemplate}
      onLoadSetupTemplatesPage={loadSetupTemplates}
      isLoadingSetupTemplates={isLoadingSetupTemplates}
    />
  );
}
